import type { HTMLAttributes } from "react";
import { cn } from "../../utils/cn";

export type BadgeVariant =
  | "default"
  | "muted"
  | "primary"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "highlight"
  | "outline";

type BadgeSize = "xs" | "sm" | "md";

type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  variant?: BadgeVariant;
  size?: BadgeSize;
};

// Semantic variants set a darker shade as the base (targets the defaulted light
// theme) and lighten via theme-specific selectors on the three dark themes. This
// mirrors Alert.tsx and clears WCAG AA for the small badge text on every theme —
// the `dark:` prefix can't be used here because `color-scheme: dark` keeps it
// always-active regardless of the active theme.
const variantClasses: Record<BadgeVariant, string> = {
  default: "bg-muted/50 text-foreground border-border/50",
  muted: "bg-muted/40 text-muted-foreground border-border/40",
  primary: "bg-primary/15 text-primary border-primary/30",
  info: "bg-sky-500/15 text-sky-700 [.theme-smoked_&]:text-sky-400 [.theme-spectre_&]:text-sky-400 [.theme-reel_&]:text-sky-400 border-sky-500/30",
  success: "bg-emerald-500/15 text-emerald-700 [.theme-smoked_&]:text-emerald-400 [.theme-spectre_&]:text-emerald-400 [.theme-reel_&]:text-emerald-400 border-emerald-500/30",
  warning: "bg-amber-500/15 text-amber-800 [.theme-smoked_&]:text-amber-400 [.theme-spectre_&]:text-amber-400 [.theme-reel_&]:text-amber-400 border-amber-500/30",
  danger: "bg-red-500/15 text-red-700 [.theme-smoked_&]:text-red-400 [.theme-spectre_&]:text-red-400 [.theme-reel_&]:text-red-400 border-red-500/30",
  // Draws the eye without ranking the value. A visual role, deliberately not a
  // severity word — this sits outside the info/warning/danger ladder rather
  // than between two rungs of it. Pink because it is the only accent hue not
  // already spoken for by info/success/warning/danger.
  highlight: "bg-pink-500/15 text-pink-700 [.theme-smoked_&]:text-pink-400 [.theme-spectre_&]:text-pink-400 [.theme-reel_&]:text-pink-400 border-pink-500/30",
  outline: "bg-transparent text-foreground border-border/50"
};

const sizeClasses: Record<BadgeSize, string> = {
  xs: "px-1.5 py-[1px] text-[10px] leading-tight",
  sm: "px-2 py-0.5 text-xs",
  md: "px-2.5 py-1 text-xs"
};

export const Badge = ({
  variant = "default",
  size = "sm",
  className,
  ...props
}: BadgeProps) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-md border font-medium",
      variantClasses[variant],
      sizeClasses[size],
      className
    )}
    {...props}
  />
);
